import { createReference, MedplumClient, QueryTypes } from "@medplum/core";
import {
  Attachment,
  Coding,
  DocumentReference,
  Patient,
  Practitioner,
  Reference,
} from "@medplum/fhirtypes";

import { ORGANIZATION_ID } from "@/constants/local-storage";
import { QuiltedHealthCodeSystems, UploadedDocumentTypesCodes } from "@/fhir/enums/quilted-health";
import { Systems } from "@/fhir/enums/systems";
import { setAccounts } from "@/utils/accounts";
import { getResourcesFromBundle, joinReferenceString } from "@/utils/medplum";
import { createAttachmentWithHash, getBinaryIdFromAttachmentUrl } from "@/utils/uploads";

import { FileFilters } from "../types/file-filters";
import { FileSortKeys } from "../types/file-sort";
import {
  NOT_VISIBLE_TO_CLIENT,
  PRACTICE_ONLY_CATEGORY_CODING,
  VISIBLE_TO_CLIENT,
} from "../types/file-uploads";
import { buildFileTypesFilter } from "../utils/file-filters";
import { FileUploadFormType } from "../zod-schemas/file-uploads";

export async function fetchUploadedFiles(
  medplum: MedplumClient,
  clientId: string,
  page: number,
  pageSize: number,
  filters: FileFilters,
  isPatient: boolean,
  sortKey?: FileSortKeys,
): Promise<[DocumentReference[], number]> {
  const query: QueryTypes = {
    patient: joinReferenceString("Patient", clientId),
    category: `${QuiltedHealthCodeSystems.UploadedDocumentSource}|manual`,
    _count: pageSize,
    ...buildFileTypesFilter(filters.fileTypes),
  };

  if (isPatient) {
    query["security-label"] = `${VISIBLE_TO_CLIENT.system}|${VISIBLE_TO_CLIENT.code}`;
  }

  const response = await medplum.searchResources("DocumentReference", {
    ...query,
    _sort: sortKey ?? "-_lastUpdated",
    _offset: (page - 1) * pageSize,
  });

  const summary = await medplum.searchResources("DocumentReference", {
    ...query,
    _summary: "count",
  });

  const total = summary?.bundle?.total ?? 0;
  return [getResourcesFromBundle("DocumentReference", response.bundle), total];
}

/**
 * Fetches DocumentReferences for a client filtered by document type (e.g. Billing).
 * Uses same category and security as main file list so these documents also appear on the Files tab.
 */
export async function fetchDocumentReferencesByType(
  medplum: MedplumClient,
  clientId: string,
  typeCode: UploadedDocumentTypesCodes,
  isPatient: boolean,
): Promise<DocumentReference[]> {
  const query: QueryTypes = {
    patient: joinReferenceString("Patient", clientId),
    category: `${QuiltedHealthCodeSystems.UploadedDocumentSource}|manual`,
    type: `${QuiltedHealthCodeSystems.UploadedDocumentTypes}|${typeCode}`,
    _sort: "-_lastUpdated",
  };

  if (isPatient) {
    query["security-label"] = `${VISIBLE_TO_CLIENT.system}|${VISIBLE_TO_CLIENT.code}`;
  }

  const response = await medplum.searchResources("DocumentReference", query);
  return getResourcesFromBundle("DocumentReference", response.bundle);
}

export interface UpsertDocumentReferenceOptions {
  medplum: MedplumClient;
  clientId: string;
  values: FileUploadFormType;
  signal: AbortSignal;
  /** The file being edited, absent when uploading a new one. */
  documentReference?: DocumentReference;
  /** Links the document to the encounter it was captured under — `context.encounter`. */
  encounterId?: string;
  /** Links the document to the statement it evidences — `context.related` on the Invoice. */
  relatedInvoiceId?: string;
  /** Replaces the default `manual` `DocumentReference.category`. */
  category?: Coding;
  /**
   * Keeps the file away from the client: forces the `billing-attachment` category and the
   * restricted security label, and stamps only the Organization account. Defaults to whether the
   * edited document already carries that category.
   */
  practiceOnly?: boolean;
}

/**
 * Uploads the file's Binary when it changed, then creates or replaces its `DocumentReference` and
 * stamps it into the practice's and, unless `practiceOnly` is set, the client's compartment.
 *
 * `upsertResource` replaces the whole resource rather than merging it. Editing an existing
 * `documentReference` therefore carries its `context` (encounter/invoice links), `category`, and
 * practice-only compartment forward by default, so a generic edit caller that only passes
 * `documentReference` can't silently drop metadata a configured caller (e.g. the statement upload
 * drawer) originally set. Pass `encounterId`, `relatedInvoiceId`, `category`, or `practiceOnly`
 * explicitly to change them.
 *
 * A practice-only file's Binary names the `DocumentReference` as its `securityContext`, so Medplum
 * refuses the file to anyone who cannot read that `DocumentReference`. A new practice-only upload
 * saves a placeholder `DocumentReference` first, so the Binary never exists without that context.
 *
 * @param options - See {@link UpsertDocumentReferenceOptions}.
 * @returns The saved `DocumentReference`, or `undefined` when no attachment could be resolved.
 * A failure before the `DocumentReference` save makes a best-effort attempt to delete the Binary
 * this call just uploaded (none for a metadata-only edit) and to mark the placeholder
 * `entered-in-error`, then rethrows. A failure after the save (for example, in `setAccounts`)
 * rethrows without deleting the Binary, since the saved `DocumentReference` already points at it.
 */
export async function upsertDocumentReference({
  medplum,
  clientId,
  values,
  signal,
  documentReference,
  encounterId,
  relatedInvoiceId,
  category,
  practiceOnly,
}: UpsertDocumentReferenceOptions): Promise<DocumentReference | undefined> {
  const file = values.file!;
  const organizationReference = {
    reference: joinReferenceString("Organization", localStorage.getItem(ORGANIZATION_ID)!),
  };
  const patientReference = { reference: joinReferenceString("Patient", clientId) };

  // Built compositionally, because `encounter` and `related` can coexist: a straight
  // `...(x && { context })` per link would let the second spread clobber the first.
  const context = {
    ...(encounterId && {
      encounter: [{ reference: joinReferenceString("Encounter", encounterId) }],
    }),
    ...(relatedInvoiceId && {
      related: [{ reference: joinReferenceString("Invoice", relatedInvoiceId) }],
    }),
    ...(!encounterId && !relatedInvoiceId && documentReference?.context),
  };
  const resolvedPracticeOnly = practiceOnly ?? isPracticeOnlyDocument(documentReference);
  const resolvedCategory = resolvedPracticeOnly
    ? PRACTICE_ONLY_CATEGORY_CODING
    : (category ?? documentReference?.category?.[0]?.coding?.[0]);

  const profile = medplum.getProfile();
  const isPatientUploadingOwnFile = profile?.resourceType === "Patient" && profile?.id === clientId;
  const visibleToClient = isPatientUploadingOwnFile ? true : values.visibleToClient;
  const securityLabel =
    visibleToClient && !resolvedPracticeOnly ? VISIBLE_TO_CLIENT : NOT_VISIBLE_TO_CLIENT;

  const currentAuthor = createReference(medplum.getProfile() as Practitioner | Patient);
  const author = getAuthor(currentAuthor, documentReference);

  const previousAttachment = documentReference?.content?.[0]?.attachment;
  let attachment: Attachment | undefined;
  let placeholder: DocumentReference | undefined;
  let upsertedDocumentReference: DocumentReference | undefined;
  try {
    if (resolvedPracticeOnly && !documentReference) {
      placeholder = await medplum.createResource<DocumentReference>({
        resourceType: "DocumentReference",
        status: "current",
        subject: patientReference,
        custodian: organizationReference,
        category: [{ coding: [PRACTICE_ONLY_CATEGORY_CODING] }],
        securityLabel: [{ coding: [NOT_VISIBLE_TO_CLIENT] }],
        content: [{ attachment: { contentType: file.type } }],
      });
    }
    const savedDocumentReference = documentReference ?? placeholder;

    attachment = await getOrCreateAttachment(
      medplum,
      documentReference,
      file,
      values.fileChanged,
      signal,
      resolvedPracticeOnly && savedDocumentReference
        ? createReference(savedDocumentReference)
        : undefined,
    );
    if (!attachment) {
      if (placeholder) await discardPlaceholder(medplum, placeholder);
      return;
    }

    upsertedDocumentReference = await medplum.upsertResource(
      {
        resourceType: "DocumentReference",
        identifier: [{ system: Systems.QH, value: values.fileName }],
        status: "current",
        type: { coding: [values.fileType!] },
        date: documentReference?.date ?? new Date().toISOString(),
        author,
        subject: patientReference,
        ...(Object.keys(context).length > 0 && { context }),
        custodian: organizationReference,
        description: values.description,
        securityLabel: [{ coding: [securityLabel] }],
        category: [
          {
            coding: [
              resolvedCategory ?? {
                system: QuiltedHealthCodeSystems.UploadedDocumentSource,
                code: "manual",
                display: "Manual",
              },
            ],
          },
        ],
        content: [
          {
            attachment,
          },
        ],
      },
      {
        _id: savedDocumentReference?.id
          ? joinReferenceString("DocumentReference", savedDocumentReference.id)
          : "DocumentReference",
      },
      { signal },
    );
  } catch (error) {
    // The DocumentReference save never happened. Only a changed file creates a Binary in this
    // attempt, so only then is `attachment` unreferenced and safe to delete before rethrowing.
    if (values.fileChanged) await deleteAttachment(medplum, attachment);
    if (placeholder) await discardPlaceholder(medplum, placeholder);
    throw error;
  }

  // The DocumentReference now points at `attachment`. A failure past this point (setAccounts,
  // cleanup of the previous Binary) must not delete it — that would leave the saved
  // DocumentReference referencing a Binary that no longer exists.
  await setAccounts(medplum, upsertedDocumentReference, [
    organizationReference,
    ...(resolvedPracticeOnly ? [] : [patientReference]),
  ]);

  if (documentReference?.id && values.fileChanged) {
    await deleteAttachment(medplum, previousAttachment);
  }

  return upsertedDocumentReference;
}

/** Whether the persisted category marks the document as practice-only (`billing-attachment`). */
function isPracticeOnlyDocument(documentReference: DocumentReference | undefined): boolean {
  return (
    documentReference?.category?.some((category) =>
      category.coding?.some(
        (coding) =>
          coding.system === PRACTICE_ONLY_CATEGORY_CODING.system &&
          coding.code === PRACTICE_ONLY_CATEGORY_CODING.code,
      ),
    ) ?? false
  );
}

function getAuthor(
  currentAuthor: Reference<Practitioner | Patient>,
  documentReference: DocumentReference | undefined,
) {
  let author;
  if (documentReference?.author?.some((author) => author.reference === currentAuthor.reference)) {
    author = documentReference.author;
  } else {
    author = [...(documentReference?.author ?? []), currentAuthor] as Reference<
      Practitioner | Patient
    >[];
  }
  return author;
}

async function deleteAttachment(medplum: MedplumClient, attachment: Attachment | undefined) {
  try {
    if (!attachment) return false;

    const binaryId = getBinaryIdFromAttachmentUrl(attachment.url);
    if (binaryId) {
      await medplum.deleteResource("Binary", binaryId);
    }
  } catch (error) {
    return false;
  }

  return true;
}

/** Marks a placeholder `DocumentReference` whose upload failed as `entered-in-error`; best effort. */
async function discardPlaceholder(medplum: MedplumClient, placeholder: DocumentReference) {
  try {
    await medplum.updateResource({ ...placeholder, status: "entered-in-error" });
  } catch (error) {
    return false;
  }

  return true;
}

async function getOrCreateAttachment(
  medplum: MedplumClient,
  documentReference: DocumentReference | undefined,
  file: File,
  hasChanged: boolean,
  signal?: AbortSignal,
  securityContext?: Reference<DocumentReference>,
): Promise<Attachment | undefined> {
  if (hasChanged) {
    return await createAttachmentWithHash(
      medplum,
      {
        data: file,
        contentType: file.type,
        filename: file.name,
        ...(securityContext && { securityContext }),
      },
      { signal },
    );
  }

  return documentReference?.content?.[0]?.attachment;
}
